import React, { useState } from 'react';
import { Form, Input, Button, DatePicker, Select, Card, Space, Typography, message, InputNumber, Row, Col } from 'antd';
import { PlusOutlined, MinusCircleOutlined, SaveOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import { financialApi } from '../../utils/financialApi';
import { extractApiUserMessage } from '../../utils/api';
import dayjs from 'dayjs';
import jalaliday from 'jalali-dayjs';

dayjs.extend(jalaliday);
const { Title, Text } = Typography;
const { Option } = Select;

const toLatinDigits = (value) => {
  if (!value) return value;
  return String(value).replace(/[۰-۹]/g, w => String.fromCharCode(w.charCodeAt(0) - 1728));
};

const DealInstallments = () => {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const handleFinish = async (values) => {
    if (!values.installments || values.installments.length === 0) {
      message.error('لطفا حداقل یک قسط اضافه کنید');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        installments: values.installments.map(inst => ({
          due_date: inst.due_date ? inst.due_date.format('YYYY-MM-DD') : null,
          amount: Number(toLatinDigits(inst.amount)),
          expected_payment_method: inst.expected_payment_method,
          notes: inst.notes || ''
        }))
      };

      await financialApi.generateInstallments(dealId, payload);
      message.success('اقساط با موفقیت ثبت شد');
      navigate('/finance/receivables-summary'); // Or back to deal details
    } catch (error) {
      message.error(extractApiUserMessage(error) || 'خطا در ثبت اقساط');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="p-4 md:p-6 max-w-[1000px] mx-auto animate-fade-in">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <Title level={3} className="m-0 text-gray-800 dark:text-gray-100 font-bold">
              تولید اقساط معامله
            </Title>
            <Text className="text-gray-500 dark:text-gray-400">
              تعریف جدول اقساط برای معامله #{dealId}
            </Text>
          </div>
          <Button icon={<ArrowRightOutlined />} onClick={() => navigate(-1)}>
            بازگشت
          </Button>
        </div>

        <Card className="shadow-sm rounded-2xl dark:bg-gray-800">
          <Form
            form={form}
            name="deal_installments_form"
            onFinish={handleFinish}
            autoComplete="off"
            layout="vertical"
            initialValues={{ installments: [{}] }}
          >
            <Form.List name="installments">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <Card 
                      key={key} 
                      size="small" 
                      className="mb-4 bg-gray-50 dark:bg-gray-700/50"
                      title={`قسط ${index + 1}`}
                      extra={
                        fields.length > 1 ? (
                          <Button 
                            type="text" 
                            danger 
                            icon={<MinusCircleOutlined />} 
                            onClick={() => remove(name)}
                          >
                            حذف
                          </Button>
                        ) : null
                      }
                    >
                      <Row gutter={16}>
                        <Col xs={24} md={8}>
                          <Form.Item
                            {...restField}
                            name={[name, 'amount']}
                            label="مبلغ (تومان)"
                            rules={[{ required: true, message: 'مبلغ الزامی است' }]}
                          >
                            <InputNumber 
                              className="w-full"
                              placeholder="مبلغ قسط"
                              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                              parser={value => value.replace(/\$\s?|(,*)/g, '')}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item
                            {...restField}
                            name={[name, 'due_date']}
                            label="تاریخ سررسید"
                            rules={[{ required: true, message: 'تاریخ سررسید الزامی است' }]}
                          >
                            <DatePicker className="w-full" placeholder="انتخاب تاریخ" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                          <Form.Item
                            {...restField}
                            name={[name, 'expected_payment_method']}
                            label="روش پرداخت پیش‌بینی‌شده"
                            rules={[{ required: true, message: 'انتخاب روش پرداخت الزامی است' }]}
                          >
                            <Select placeholder="روش پرداخت">
                              <Option value="cheque">چک صیادی</Option>
                              <Option value="bank_transfer">حواله بانکی</Option>
                              <Option value="cash">نقدی</Option>
                              <Option value="pos">کارت‌خوان (POS)</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col xs={24}>
                          <Form.Item
                            {...restField}
                            name={[name, 'notes']}
                            label="توضیحات"
                          >
                            <Input placeholder="یادداشت برای این قسط (مثلاً قسط اول - چک صیادی)" />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  
                  <Form.Item>
                    <Button 
                      type="dashed" 
                      onClick={() => add()} 
                      block 
                      icon={<PlusOutlined />}
                      className="border-dashed"
                    >
                      افزودن قسط جدید
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            <Form.Item className="mb-0 text-left">
              <Space>
                <Button onClick={() => navigate(-1)}>انصراف</Button>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={loading}
                  icon={<SaveOutlined />}
                  className="bg-blue-600"
                >
                  ثبت اقساط
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </div>
    </MainLayout>
  );
};

export default DealInstallments;
